import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useKeyboardHeight, useStandaloneAppHeight, useTheme } from '@talk2nick/ui';
import { publishPresence } from '@talk2nick/shared';
import { useTranslation } from 'react-i18next';

import { HomeRoute } from '../routes/HomeRoute';
import ShareRoute from '../routes/ShareRoute';
import { AppBottomNav } from '../components/AppBottomNav';
import { ConnectionStatus } from '../components/ConnectionStatus';
import { NotificationHost } from '../components/NotificationHost';
import { PushPermissionPrompt } from '../components/PushPermissionPrompt';
import { TranslationLimitBanner } from '../components/TranslationLimitBanner';
import { TranslationPausedBanner } from '../components/TranslationPausedBanner';
import { UpdateBanner } from '../components/UpdateBanner';
import { useAuth } from '../features/auth/useAuth';
import { LockGate } from '../features/auth/LockGate';
import { usePushRegistrationEffect } from '../features/push/usePushRegistration';

const ChatsRoute = lazy(() => import('../routes/ChatsRoute'));
const SettingsRoute = lazy(() => import('../routes/SettingsRoute'));
const TranslationSettingsRoute = lazy(() => import('../routes/TranslationSettingsRoute'));
const JoinRoute = lazy(() => import('../routes/JoinRoute'));
const AdminConsoleRoute = lazy(() => import('../routes/AdminConsoleRoute'));
const PrivacyRoute = lazy(() => import('../routes/PrivacyRoute'));
const NotificationsRoute = lazy(() => import('../routes/NotificationsRoute'));
const AccountRoute = lazy(() => import('../routes/AccountRoute'));
const LanguageRoute = lazy(() => import('../routes/LanguageRoute'));

function BootScreen({ label }: { label: string }) {
  return (
    <div className="app-shell items-center justify-center bg-[var(--bg-app)]">
      <p className="text-sm text-[var(--text-secondary)]">{label}</p>
    </div>
  );
}

export function App() {
  // iOS 오버레이 키보드 높이 → --kb-height CSS 변수 (PLAN 6-3장)
  useKeyboardHeight();
  // 설치형 PWA의 viewport 단위 59pt 오차 → --app-height (하단 요소가 가장자리에 붙도록)
  useStandaloneAppHeight();
  const location = useLocation();
  const isJoinOrShare =
    location.pathname.startsWith('/join/') || location.pathname.startsWith('/share');
  const { t } = useTranslation();
  // 인증 상태는 여기서 한 번만 구독하고 라우트에 내려준다 (잠금 게이트도 같은 값을 쓴다)
  const auth = useAuth();
  // 전역 테마 — 방이 굳힌 주제가 없으면 여기 설정이 전체를 이끈다
  useTheme(auth.profile?.settings?.theme ?? 'system');
  // 로그인 + 푸시 허용 상태면 이 기기의 FCM 토큰을 조용히 등록한다 (PLAN 5-8-8장)
  usePushRegistrationEffect(auth);

  // RTDB 온라인/activeConvo presence 등록 — 연결이 끊기면 onDisconnect가 자동 정리한다
  useEffect(() => {
    const uid = auth.firebaseUser?.uid;
    if (auth.status !== 'authenticated' || !uid) return;
    return publishPresence(uid, 'web');
  }, [auth.status, auth.firebaseUser?.uid]);

  return (
    <div className="app-shell">
      {/* 서비스워커/앱 새 버전 알림 — 최상단에 노출되어 모든 화면(로그인, 잠금, 채팅)에서 즉시 확인 가능 */}
      <UpdateBanner />
      {/* 연결 상태 UI (오프라인 배너 / 재접속 진행바 / 동기화 토스트) */}
      <ConnectionStatus />
      <LockGate auth={auth}>
        {/* 상단 배너 슬롯 — 헤더(앱바) 위에 얹고, 프롬프트와 인앱 알림이 같은 자리를 덮지 않도록
            한 컨테이너에서 세로로 쌓는다. 프롬프트가 위에 있어 알림 도착에도 자리가 흔들리지 않는다 */}
        <div className="pointer-events-none fixed inset-x-0 top-[calc(var(--safe-top)+var(--space-2))] z-[var(--z-banner)] flex flex-col items-center gap-2 px-3">
          {/* iOS/갤럭시 실시간 알림 허용 1-탭 유도 프롬프트 */}
          {auth.status === 'authenticated' && (
            <PushPermissionPrompt userId={auth.firebaseUser?.uid ?? null} />
          )}
          {/* 인앱 알림 배너 + 미읽음 집계 — 목록 리스너 1개를 공유한다 (PLAN 5-8-9장) */}
          {auth.status === 'authenticated' && <NotificationHost auth={auth} />}
        </div>
        {/* 번역 한도 안내 (PLAN 5-7-5장) — 하단에서 하루 한 번 올라온다 */}
        {auth.status === 'authenticated' && <TranslationLimitBanner auth={auth} />}
        {/* 전역 번역 일시정지 (일일 비용 상한 초과) — 모든 화면에 원인을 밝힌다 */}
        <TranslationPausedBanner />
        {/* 로그아웃하면 로그인 화면으로 돌려보낸다 — 라우트 가드가 없으면 현재 경로에
            남아 프로필 없는 계정 화면(로그아웃 버튼만)이 그대로 노출된다 (초대장/공유 진입점은 제외) */}
        {auth.status === 'unauthenticated' && !isJoinOrShare && <Navigate to="/" replace />}
        <Suspense fallback={<BootScreen label={t('common.loading')} />}>
          <Routes>
            <Route path="/" element={<HomeRoute auth={auth} />} />
            <Route path="/chats" element={<ChatsRoute auth={auth} />} />
            <Route path="/chat/:convoId" element={<ChatsRoute auth={auth} />} />
            {/* 티켓 진입점 — 이메일로 받은 초대 링크 (PLAN 0-B 변경 9장) */}
            <Route path="/join/:token" element={<JoinRoute auth={auth} />} />
            {/* 공유 수신 진입점 — 서비스워커가 POST를 캐시하고 여기로 리다이렉트 */}
            <Route path="/share" element={<ShareRoute auth={auth} />} />
            <Route path="/settings" element={<SettingsRoute auth={auth} />} />
            <Route
              path="/settings/translation"
              element={<TranslationSettingsRoute auth={auth} />}
            />
            {/* 설정 하위 화면 — SettingsRoute가 링크하는 4개 (M5) */}
            <Route path="/settings/privacy" element={<PrivacyRoute auth={auth} />} />
            <Route path="/settings/notifications" element={<NotificationsRoute auth={auth} />} />
            <Route path="/settings/account" element={<AccountRoute auth={auth} />} />
            <Route path="/settings/language" element={<LanguageRoute auth={auth} />} />
            {/* 신고 대기열 — 어드민 클레임 보유자만 (Rules도 같은 조건) */}
            <Route path="/admin" element={<AdminConsoleRoute auth={auth} />} />
            <Route path="/admin/reports" element={<AdminConsoleRoute auth={auth} />} />
            <Route path="*" element={<HomeRoute auth={auth} />} />
          </Routes>
        </Suspense>
        {auth.status === 'authenticated' && <AppBottomNav auth={auth} />}
      </LockGate>
    </div>
  );
}
